import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, TextField, CircularProgress, Paper, List, ListItem, ListItemButton,
  Chip, Typography, InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import productoService from '../../../services/productoService';
import {
  evaluarUsoProductoDocumento,
  esDiscontinuadoBloqueadoParaAgregar,
  esTipoDiscontinuado,
  filtrarProductosInactivos,
  cargarProductoParaDocumento,
  mensajeArticuloDiscontinuadoBloqueado,
  opcionesGetProductoDocumento,
  paramsBusquedaProductoDocumento,
  type ModoBloqueoArtDiscontinuados,
} from '../../../utils/bloqueaArtDiscontinuados';
import articuloXListaPrecioService from '../../../services/articuloXListaPrecioService';
import { resolverPrecioDesdeLista, obtenerPrecioBaseProducto } from '../../../utils/precioListaPedido';
import {
  type ContextoUmVenta,
  resolverUmYPrecioDocumento,
  resolverUmYPrecioDesdeDetalle,
} from '../../../utils/umVentaDocumento';
import { aplicarStockEnUmLinea, fetchStockDisponibleArticulo, fetchStockDisponibleEnUm } from '../../../utils/stockUmVenta';
import { formatCurrency, formatStockCantidad } from '../../../utils/formatters';
import { getProductoFallbackImage, getProductoImageBySize } from '../../../utils/productImage';
import { esEntradaCodigoBarra } from '../../../utils/codigoBarraVenta';
import { getApiErrorMessage } from '../../../utils/apiError';
import { SuggestionItem } from './types';
import { aPrecioDisplay, multiplicadorIvaProducto, round2 } from './preciosIvaDisplay';
import { esCondIvaMonotributo } from '../../../constants/condIvaCompania';
import ProductoInfo360Dialog from '../../inventario/info360/ProductoInfo360Dialog';
import ProductoInfo360ListButton from '../../inventario/info360/ProductoInfo360ListButton';

export type { SuggestionItem } from './types';

const PAGE_SIZE = 20;

const descripcionSx = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
  lineHeight: 1.35,
  wordBreak: 'break-word' as const,
};

interface Props {
  nroLista?: string;
  disabled?: boolean;
  /** pedido → umrefPed; factura → umrefFac */
  contextoUm?: ContextoUmVenta;
  tipoIvaEmision?: string;
  condIvaEmisor?: string | null;
  preciosConIva?: boolean;
  /** Filtra por TipoArticulo (coma-separados), p. ej. "FIN". */
  tiposArticulo?: string;
  placeholder?: string;
  onAgregarProducto: (item: SuggestionItem) => void;
}

const tipoArticuloPermitido = (tipo: string | null | undefined, tiposArticulo?: string): boolean => {
  const allowed = String(tiposArticulo ?? '')
    .split(',')
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);
  if (allowed.length === 0) return true;
  return allowed.includes(String(tipo ?? '').trim().toUpperCase());
};

const StockBadge: React.FC<{ stock: number | null | undefined; um?: string | null }> = ({ stock, um }) => {
  if (stock == null) return null;
  if (stock <= 0)
    return <Chip label="Sin stock" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />;
  const umTxt = String(um ?? '').trim();
  const label = `Stock: ${formatStockCantidad(stock)}${umTxt ? ` ${umTxt}` : ''}`;
  if (stock < 10)
    return <Chip label={label} size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />;
  return <Chip label={label} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />;
};

const FacturaPOSSearchBar: React.FC<Props> = ({
  nroLista,
  disabled,
  contextoUm = 'factura',
  tipoIvaEmision = '',
  condIvaEmisor,
  preciosConIva = false,
  tiposArticulo,
  placeholder = 'Código o descripción… ↑↓ y Enter',
  onAgregarProducto,
}) => {
  const mostrarLeyendaImpuestos = preciosConIva && !esCondIvaMonotributo(condIvaEmisor);
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const [info360Articulo, setInfo360Articulo] = useState<string | null>(null);
  const [modoBloqueoDis, setModoBloqueoDis] = useState<ModoBloqueoArtDiscontinuados>(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const enriquecerProductos = useCallback(async (items: any[]): Promise<SuggestionItem[]> => {
    return Promise.all(
      items.map(async (p) => {
        let precio = obtenerPrecioBaseProducto(p);
        let umPrecioLista: string | undefined;

        if (nroLista) {
          try {
            const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(
              nroLista,
              p.codigoArticulo,
            );
            const resultado = resolverPrecioDesdeLista(p, reg);
            if (resultado.tipo !== 'sinPrecio') {
              precio = resultado.precioLista;
              umPrecioLista = (reg.um || '').trim() || undefined;
            }
          } catch {
            /* sin precio en lista, usa precio base */
          }
        }

        let productoFull = p;
        try {
          // Solo cargar datos; la confirmación DIS (modo 2) es al seleccionar/agregar.
          productoFull = await cargarProductoParaDocumento(p.codigoArticulo, 'VENTAS');
        } catch {
          /* usar datos de búsqueda */
        }

        const tipoArt = String(productoFull?.tipoArticulo ?? p.tipoArticulo ?? '').trim().toUpperCase();

        let stock: number | null = null;
        try {
          stock = await fetchStockDisponibleArticulo(p.codigoArticulo, {
            tipoArticulo: productoFull?.tipoArticulo ?? p.tipoArticulo ?? null,
          });
        } catch {
          /* sin stock disponible */
        }

        let umPrecio;
        let detalleEq: Awaited<ReturnType<typeof productoService.getEquivalenciasUmDetalle>> | null = null;
        try {
          detalleEq = await productoService.getEquivalenciasUmDetalle(String(p.codigoArticulo));
          umPrecio = resolverUmYPrecioDesdeDetalle(
            productoFull,
            precio,
            contextoUm,
            detalleEq,
            umPrecioLista,
          );
        } catch {
          umPrecio = resolverUmYPrecioDocumento(
            productoFull,
            precio,
            contextoUm,
            [],
            umPrecioLista,
          );
        }
        precio = round2(umPrecio.precioUnitario);

        if (tipoArt !== 'KIT') {
          if (stock != null && umPrecio.um) {
            stock = aplicarStockEnUmLinea(stock, umPrecio.um, detalleEq, productoFull);
          } else if (umPrecio.um) {
            stock = await fetchStockDisponibleEnUm(String(p.codigoArticulo), umPrecio.um, detalleEq, productoFull);
          }
        }

        const multiplicador = preciosConIva
          ? multiplicadorIvaProducto(productoFull, tipoIvaEmision, condIvaEmisor)
          : 1;

        return {
          codigoArticulo: p.codigoArticulo,
          descripcion: p.descripcion,
          precioUnitario: precio,
          precioUnitarioDisplay: preciosConIva
            ? aPrecioDisplay(precio, multiplicador)
            : undefined,
          um: umPrecio.um,
          stockDisponible: stock,
          imageUrl: getProductoImageBySize(productoFull, 'thumbnail'),
          productoIva: productoFull,
          stockMin: Number(productoFull?.stockMin ?? p.stockMin ?? 0) || 0,
          tipoArticulo: productoFull?.tipoArticulo ?? p.tipoArticulo ?? null,
        };
      }),
    );
  }, [nroLista, preciosConIva, tipoIvaEmision, condIvaEmisor, contextoUm]);

  const aplicarSemaforoCobertura = useCallback(async (items: SuggestionItem[]): Promise<SuggestionItem[]> => {
    if (items.length === 0) return items;
    try {
      const semaforos = await productoService.getProductosInfo360Semaforo(
        items.map((i) => i.codigoArticulo),
      );
      const byCodigo = new Map(
        semaforos.map((s) => [s.codigoArticulo.trim().toUpperCase(), s]),
      );
      return items.map((item) => {
        const s = byCodigo.get(item.codigoArticulo.trim().toUpperCase());
        if (!s) return item;
        return {
          ...item,
          coberturaDias: s.coberturaDias,
          demandaMensual6m: s.demandaMensual6m,
          leadTimeDias: s.leadTimeDias,
        };
      });
    } catch {
      return items;
    }
  }, []);

  const resetBusqueda = useCallback(() => {
    setSuggestions([]);
    setPageNumber(1);
    setTotalCount(0);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  const buscarProductos = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) {
      resetBusqueda();
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    setLoading(true);
    setPageNumber(1);

    try {
      const paramsBloqueo = await paramsBusquedaProductoDocumento('VENTAS');
      setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
      const res = await productoService.getProductos({
        searchTerm: trimmed,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        ...(tiposArticulo ? { tiposArticulo } : {}),
        ...paramsBloqueo,
      });

      if (requestId !== searchRequestIdRef.current) return;

      const items: any[] = filtrarProductosInactivos(res.items ?? []);
      const enriched = await aplicarSemaforoCobertura(await enriquecerProductos(items));

      if (requestId !== searchRequestIdRef.current) return;

      setTotalCount(res.totalCount ?? enriched.length);
      setSuggestions(enriched);
      setOpen(enriched.length > 0);
      setHighlightedIndex(enriched.length > 0 ? 0 : -1);
      itemRefs.current = [];
    } catch {
      if (requestId !== searchRequestIdRef.current) return;
      resetBusqueda();
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [enriquecerProductos, aplicarSemaforoCobertura, resetBusqueda, tiposArticulo]);

  const cargarMas = useCallback(async () => {
    const trimmed = term.trim();
    if (!trimmed || loading || loadingMore) return;
    if (suggestions.length >= totalCount) return;

    const nextPage = pageNumber + 1;
    const requestId = searchRequestIdRef.current;
    setLoadingMore(true);

    try {
      const paramsBloqueo = await paramsBusquedaProductoDocumento('VENTAS');
      setModoBloqueoDis(paramsBloqueo.modoBloqueoArtDiscontinuados ?? 1);
      const res = await productoService.getProductos({
        searchTerm: trimmed,
        pageNumber: nextPage,
        pageSize: PAGE_SIZE,
        ...(tiposArticulo ? { tiposArticulo } : {}),
        ...paramsBloqueo,
      });

      if (requestId !== searchRequestIdRef.current) return;

      const items: any[] = filtrarProductosInactivos(res.items ?? []);
      if (items.length === 0) return;

      const enriched = await aplicarSemaforoCobertura(await enriquecerProductos(items));

      if (requestId !== searchRequestIdRef.current) return;

      setTotalCount(res.totalCount ?? totalCount);
      setPageNumber(nextPage);
      setSuggestions((prev) => {
        const existing = new Set(prev.map((s) => s.codigoArticulo.trim()));
        const nuevos = enriched.filter((s) => !existing.has(s.codigoArticulo.trim()));
        return [...prev, ...nuevos];
      });
      setOpen(true);
    } catch {
      /* mantener resultados actuales */
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [term, loading, loadingMore, suggestions.length, totalCount, pageNumber, enriquecerProductos, aplicarSemaforoCobertura, tiposArticulo]);

  const hayMasResultados = suggestions.length < totalCount;

  const handleListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el || loading || loadingMore || !hayMasResultados) return;

    const threshold = 48;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (nearBottom) void cargarMas();
  }, [loading, loadingMore, hayMasResultados, cargarMas]);

  useEffect(() => {
    if (!open || loading || loadingMore || !hayMasResultados) return;
    const el = listScrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 1) void cargarMas();
  }, [open, loading, loadingMore, hayMasResultados, suggestions.length, cargarMas]);

  useEffect(() => {
    if (highlightedIndex < 0 || loading || loadingMore || !hayMasResultados) return;
    if (highlightedIndex >= suggestions.length - 3) void cargarMas();
  }, [highlightedIndex, suggestions.length, loading, loadingMore, hayMasResultados, cargarMas]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarProductos(term), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, buscarProductos]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = async (item: SuggestionItem) => {
    if (!tipoArticuloPermitido(item.tipoArticulo, tiposArticulo)) {
      setBarcodeError(
        tiposArticulo
          ? `Solo se permiten artículos de tipo ${tiposArticulo}.`
          : 'Tipo de artículo no permitido.',
      );
      return;
    }
    if (esDiscontinuadoBloqueadoParaAgregar(item.tipoArticulo, modoBloqueoDis)) {
      setBarcodeError(mensajeArticuloDiscontinuadoBloqueado(item.codigoArticulo));
      return;
    }
    const decision = await evaluarUsoProductoDocumento(
      {
        codigoArticulo: item.codigoArticulo,
        tipoArticulo: item.tipoArticulo,
        descripcion: item.descripcion,
      },
      'VENTAS',
    );
    if (!decision.permitido) {
      setBarcodeError(decision.mensaje);
      return;
    }
    onAgregarProducto(item);
    setTerm('');
    setBarcodeError(null);
    resetBusqueda();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const resolverYElegir = useCallback(async (codigo: string) => {
    setLoading(true);
    setBarcodeError(null);
    setOpen(false);
    try {
      const apiOpts = await opcionesGetProductoDocumento('VENTAS');
      const resolved = await productoService.resolverCodigoBarra(codigo, nroLista, apiOpts);
      const tipoResuelto = (resolved.producto as { tipoArticulo?: string | null } | undefined)?.tipoArticulo;
      if (!tipoArticuloPermitido(tipoResuelto, tiposArticulo)) {
        setBarcodeError(
          tiposArticulo
            ? `Solo se permiten artículos de tipo ${tiposArticulo}.`
            : 'Tipo de artículo no permitido.',
        );
        return;
      }
      const decisionBarra = await evaluarUsoProductoDocumento(
        {
          codigoArticulo: resolved.codigoArticulo,
          tipoArticulo: tipoResuelto,
          descripcion: resolved.descripcion,
        },
        'VENTAS',
      );
      if (!decisionBarra.permitido) {
        setBarcodeError(decisionBarra.mensaje);
        return;
      }
      let stock: number | null = null;
      try {
        stock = await fetchStockDisponibleArticulo(resolved.codigoArticulo, {
          tipoArticulo: (resolved.producto as { tipoArticulo?: string | null } | undefined)?.tipoArticulo ?? null,
        });
      } catch {
        /* sin stock */
      }

      const multiplicador = preciosConIva
        ? multiplicadorIvaProducto(resolved.producto, tipoIvaEmision, condIvaEmisor)
        : 1;

      let um = resolved.um ?? '';
      let precioUnitario = Number(resolved.precioUnitario) || 0;
      const productoUm = resolved.producto ?? { codigoArticulo: resolved.codigoArticulo, um: resolved.um };
      try {
        const detalle = await productoService.getEquivalenciasUmDetalle(String(resolved.codigoArticulo));
        const r = resolverUmYPrecioDesdeDetalle(productoUm, precioUnitario, contextoUm, detalle);
        um = r.um || um;
        precioUnitario = round2(r.precioUnitario);
        if (stock != null && um) {
          stock = aplicarStockEnUmLinea(stock, um, detalle, productoUm);
        } else if (um) {
          stock = await fetchStockDisponibleEnUm(String(resolved.codigoArticulo), um, detalle);
        }
      } catch {
        /* conservar UM/precio del resolver */
      }

      onAgregarProducto({
        codigoArticulo: resolved.codigoArticulo,
        descripcion: resolved.descripcion,
        precioUnitario,
        precioUnitarioDisplay: preciosConIva
          ? aPrecioDisplay(precioUnitario, multiplicador)
          : undefined,
        cantidad: resolved.cantidad,
        um,
        stockDisponible: stock,
        productoIva: resolved.producto,
      });
      setTerm('');
      resetBusqueda();
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err) {
      setBarcodeError(getApiErrorMessage(err, 'No se pudo resolver el código de barras.'));
    } finally {
      setLoading(false);
    }
  }, [nroLista, onAgregarProducto, preciosConIva, resetBusqueda, tipoIvaEmision, condIvaEmisor, contextoUm]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (e.key === 'Enter') {
      const trimmed = term.trim();
      if (esEntradaCodigoBarra(trimmed)) {
        e.preventDefault();
        void resolverYElegir(trimmed);
        return;
      }
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0;
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return suggestions.length - 1;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const item = suggestions[idx];
      if (item) handleSelect(item);
    }
  };

  const handleClear = () => {
    setTerm('');
    setBarcodeError(null);
    resetBusqueda();
    inputRef.current?.focus();
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        placeholder={placeholder}
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          if (barcodeError) setBarcodeError(null);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        disabled={disabled}
        size="small"
        error={Boolean(barcodeError)}
        helperText={barcodeError ?? undefined}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" color="action" />}
            </InputAdornment>
          ),
          endAdornment: term ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} edge="end">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
      />

      {open && !info360Articulo && suggestions.length > 0 && (
        <Paper
          ref={listScrollRef}
          onScroll={handleListScroll}
          elevation={6}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1200,
            maxHeight: 340,
            overflowY: 'auto',
            mt: 0.5,
            borderRadius: 2,
          }}
        >
          <List dense disablePadding>
            {suggestions.map((s, idx) => {
              const disBloqueado = esDiscontinuadoBloqueadoParaAgregar(s.tipoArticulo, modoBloqueoDis);
              const esDis = esTipoDiscontinuado(s.tipoArticulo);
              return (
              <ListItem
                key={s.codigoArticulo}
                disablePadding
                divider={idx < suggestions.length - 1 || loadingMore}
                secondaryAction={
                  <ProductoInfo360ListButton
                    producto={{
                      codigoArticulo: s.codigoArticulo,
                      cantidad: s.stockDisponible,
                      stockMin: s.stockMin,
                      tipoArticulo: s.tipoArticulo,
                      coberturaDias: s.coberturaDias,
                      demandaMensual6m: s.demandaMensual6m,
                      leadTimeDias: s.leadTimeDias,
                    }}
                    onClick={() => {
                      setOpen(false);
                      setInfo360Articulo(s.codigoArticulo);
                    }}
                    dense
                  />
                }
                sx={{ pr: 5.5 }}
              >
                <ListItemButton
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  selected={idx === highlightedIndex}
                  disabled={disBloqueado}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onClick={() => handleSelect(s)}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 1.25,
                    opacity: disBloqueado ? 0.72 : 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      bgcolor: 'grey.50',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      component="img"
                      src={s.imageUrl ?? getProductoFallbackImage()}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        e.currentTarget.src = getProductoFallbackImage();
                      }}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Tooltip
                      title={
                        disBloqueado
                          ? mensajeArticuloDiscontinuadoBloqueado(s.codigoArticulo)
                          : s.descripcion
                      }
                      placement="top-start"
                      enterDelay={400}
                      leaveDelay={0}
                      disableInteractive
                    >
                      <Typography variant="body2" component="span" sx={descripcionSx}>
                        {s.descripcion}
                      </Typography>
                    </Tooltip>
                    <Box display="flex" alignItems="center" gap={1} mt={0.35} flexWrap="wrap">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace', flexShrink: 0, minWidth: 48 }}
                      >
                        {s.codigoArticulo}
                      </Typography>
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        {preciosConIva ? 'Precio Final: ' : ''}
                        {formatCurrency(s.precioUnitarioDisplay ?? s.precioUnitario)}
                        {mostrarLeyendaImpuestos ? ' c/imp.' : ''}
                      </Typography>
                      {s.um && (
                        <Typography variant="caption" color="text.secondary">
                          / {s.um}
                        </Typography>
                      )}
                      {esDis ? (
                        <Chip
                          label="Discontinuado"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      ) : null}
                      <StockBadge stock={s.stockDisponible} um={s.um} />
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            );
            })}
            {loadingMore && (
              <ListItem disablePadding>
                <Box sx={{ py: 1.25, display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <CircularProgress size={18} />
                </Box>
              </ListItem>
            )}
          </List>
        </Paper>
      )}
      <ProductoInfo360Dialog
        open={info360Articulo != null}
        codigoArticulo={info360Articulo}
        onClose={() => setInfo360Articulo(null)}
      />
    </Box>
  );
};

export default FacturaPOSSearchBar;
